import type { Stage } from './board.types';

// Sub-task for complex automation actions
export interface SubTask {
  label: string;
  description: string;
}

// Automation action button configuration
export interface AutomationAction {
  label: string;
  description: string;
  icon: string;
  action?: string;
  subTasks?: SubTask[];
}

// Type for the stage automation buttons map
export type StageAutomationButtonsMap = Record<Stage, AutomationAction[]>;

export const stageAutomationButtons: StageAutomationButtonsMap = {
  Ideation: [
    {
      label: "Generate AI Expansion",
      description: "Create SWOT analysis, user personas, UVP, and alternative approaches using AI.",
      icon: "🤖",
      action: "ai-expand"
    },
    {
      label: "Set Up Project Space",
      description: "Create a dedicated online workspace with a folder for documents, files, and notes.",
      icon: "📁"
    },
    {
      label: "Create App Boilerplate",
      description: "Generate a complete application boilerplate with frontend, backend, and database setup.",
      icon: "🚀"
    }
  ],

  Research: [
    {
      label: "Create Simple Idea Page",
      description: "Publish a one-page description of your idea with a signup form.",
      icon: "🌐"
    },
    {
      label: "Enable Visitor Tracking",
      description: "Add basic analytics to see how many people visit and sign up.",
      icon: "📊"
    },
    {
      label: "Start Email List",
      description: "Set up a mailing list to collect and contact interested people.",
      icon: "📧"
    }
  ],

  PoC: [
    {
      label: "Create Prototype",
      description: "Build a basic working version of your idea to test functionality.",
      icon: "🔧"
    },
    {
      label: "Set Up Testing Environment",
      description: "Prepare a controlled environment to test your prototype.",
      icon: "🧪"
    },
    {
      label: "Define Success Metrics",
      description: "Establish clear criteria to measure prototype success.",
      icon: "📈"
    }
  ],

  Marketing: [
    {
      label: "Prepare Visual Identity (Optional)",
      description: "Generate a simple logo and color palette for early materials.",
      icon: "🎨"
    },
    {
      label: "Draft AI Messages",
      description: "Generate your pitch, problem statement, and benefits using your idea details.",
      icon: "🤖"
    },
    {
      label: "Prepare Domain & Email",
      description: "Secure your domain, set up email, and ensure deliverability.",
      icon: "🌍",
      subTasks: [
        { label: "Register Domain in Azure DNS", description: "Purchase and register your project's domain name." },
        { label: "Add DNS Records: DMARC & DKIM", description: "Set up DMARC and DKIM to improve email deliverability." },
        { label: "Configure Domain in Microsoft 365", description: "Add SPF, TXT, and CNAME records in O365 Admin using the setup wizard." },
        { label: "Create Domain Email Aliases", description: "Add new email addresses for your domain." },
        { label: "Link Support Email (Optional)", description: "Route support@yourdomain to a monitored inbox." },
        { label: "Set Up Blacklist Monitoring (Optional)", description: "Monitor your domain for blacklisting issues via MXToolbox." }
      ]
    },
    {
      label: "Publish Main Website",
      description: "Deploy a professional marketing site with signup/contact forms.",
      icon: "🚀"
    }
  ],

  Sales: [
    {
      label: "Prepare Outreach List",
      description: "Build or import a list of people or companies to contact.",
      icon: "👥"
    },
    {
      label: "Send First Outreach Campaign",
      description: "Automatically send the first batch of personalized emails.",
      icon: "📤"
    },
    {
      label: "Track Engagement",
      description: "Monitor email opens, clicks, and replies.",
      icon: "📊"
    },
    {
      label: "Share Booking Link",
      description: "Provide an online link so prospects can book a meeting.",
      icon: "📅"
    }
  ],

  Validated: [
    {
      label: "Compile Feedback & Metrics",
      description: "Collect website, email, and meeting results in one summary.",
      icon: "📋"
    },
    {
      label: "Decide Next Steps",
      description: "Choose whether to proceed, improve, or stop.",
      icon: "🎯"
    },
    {
      label: "Plan Production Launch",
      description: "If proceeding, prepare tasks for security, scaling, and deployment.",
      icon: "📋"
    }
  ]
};
